import React from 'react'
import {
  InformationCircleFillIcon,
  ExclamationMarkHexagonFillIcon,
  CheckCircleFillIcon,
  ExclamationMarkTriangleFillIcon,
  CircleDashIcon,
} from '@grupoboticario/flora-react-icons'
import { forwardRef } from '@grupoboticario/flora-react-system'
import { FloraHelperTextProps } from './types'
import { StyledHelperText, Content, LoadingIcon } from './styles'

/**
 * @component
 * @name FloraHelperText
 * @description Elemento interativo para realizar ações em aplicativos e sites. Ele pode conter texto, ícones ou ambos, variar em estilo e aparência, oferecer feedback visual quando pressionado e desempenha um papel crucial na usabilidade, fornecendo uma maneira intuitiva e consistente para os usuários executarem ações específicas, como enviar formulários, salvar alterações ou navegar para outras páginas. Além disso, os botões devem ser projetados com acessibilidade em mente, para garantir que todos os usuários possam interagir com eles de forma eficaz.
 * @tutorial https://florabook.gb.tech/v2/index.html?path=/docs/core-components-helpertext
 */
export const FloraHelperText = forwardRef<'div', FloraHelperTextProps>(
  ({ status = 'neutral', surfaceColor, children, id }, forwardedRef) => {
    return (
      <div aria-live="polite">
        {children && (
          <StyledHelperText
            ref={forwardedRef}
            as="div"
            status={status}
            surfaceColor={surfaceColor}
            data-testid="helper-text"
          >
            {status === 'neutral' && (
              <InformationCircleFillIcon data-testid="icon-neutral" aria-hidden="true" size={16} />
            )}
            {status === 'success' && (
              <CheckCircleFillIcon data-testid="icon-success" aria-hidden="true" size={16} />
            )}
            {status === 'warning' && (
              <ExclamationMarkTriangleFillIcon
                data-testid="icon-warning"
                aria-hidden="true"
                size={16}
              />
            )}
            {status === 'error' && (
              <ExclamationMarkHexagonFillIcon
                data-testid="icon-error"
                aria-hidden="true"
                size={16}
              />
            )}
            {status === 'loading' && (
              <LoadingIcon data-testid="icon-loading" aria-hidden="true" size={16} />
            )}
            {status === 'disabled' && (
              <CircleDashIcon data-testid="icon-disabled" aria-hidden="true" size={16} />
            )}
            <Content id={id}>{children}</Content>
          </StyledHelperText>
        )}
      </div>
    )
  },
)
